import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import imageCompression from 'browser-image-compression';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { AppHeader } from '../components/AppHeader';
import { ProfileFormFields } from '../components/profile/ProfileFormFields';
import {
  deleteProfileAvatar,
  getProfile,
  saveProfile,
  uploadProfileAvatar,
} from '../lib/api/profile';
import { getInvitePreview, acceptInvite } from '../lib/api/invite';
import { showSuccessToast, showErrorToast } from '../lib/toast';
import { ApiError } from '../lib/api/core';
import { consumeRegisterForAfterProfileSave } from '../lib/registerForIntent';
import { useAuth } from '../contexts/AuthContext';
import { type BmabTitleCode } from '@swiss-magic/shared-types';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { isSafeRedirectPath } from '@/lib/safeRedirect';

// 006 follow-up — pull first/last name out of the OAuth provider's
// user_metadata (Google sends given_name/family_name/full_name; other
// providers vary). Used as a fallback prefill when the user has no
// saved profile and no invite. Returns blanks when nothing usable
// is in metadata so the form stays empty rather than showing nulls.
function prefillFromAuthMetadata(
  metadata: Record<string, unknown> | null | undefined,
): { firstName: string; lastName: string } {
  if (!metadata) return { firstName: '', lastName: '' };
  const str = (k: string): string =>
    typeof metadata[k] === 'string' ? (metadata[k] as string).trim() : '';
  const given = str('given_name');
  const family = str('family_name');
  if (given || family) return { firstName: given, lastName: family };
  const full = str('full_name') || str('name');
  if (!full) return { firstName: '', lastName: '' };
  const parts = full.split(/\s+/);
  return {
    firstName: parts[0] ?? '',
    lastName: parts.length > 1 ? parts.slice(1).join(' ') : '',
  };
}

const SUPPORTED_AVATAR_MIME = /^image\/(png|jpeg|webp)$/;

async function compressForAvatar(file: File, t: TFunction): Promise<File> {
  if (!SUPPORTED_AVATAR_MIME.test(file.type)) {
    throw new Error(t('profile.avatar_unsupported_format'));
  }
  return imageCompression(file, {
    maxSizeMB: 1,
    maxWidthOrHeight: 1024,
    useWebWorker: true,
    initialQuality: 0.85,
  });
}

export function ProfilePage() {
  const { t } = useTranslation('player');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [city, setCity] = useState('');
  const [country, setCountry] = useState('');
  const [bio, setBio] = useState('');
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [chessRating, setChessRating] = useState('');
  const [chessFideId, setChessFideId] = useState('');
  const [chessClub, setChessClub] = useState('');
  const [sex, setSex] = useState<'' | 'm' | 'f'>('');
  const [birthYear, setBirthYear] = useState('');
  const [chessTitle, setChessTitle] = useState('');
  const [backgammonClub, setBackgammonClub] = useState('');
  const [bmabTitle, setBmabTitle] = useState<BmabTitleCode | ''>('');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const invite = searchParams.get('invite');
    (async () => {
      try {
        const profile = await getProfile();
        // A blank profile row is auto-created at registration, so a row may
        // exist before the user has saved anything. Treat it as "saved" only
        // once it carries a name — otherwise fall through to invite / OAuth
        // prefill below (story 006a / invite flow). The avatar still comes
        // from the row, so a photo added before saving survives a reload.
        const hasSavedProfile =
          !!profile && !!(profile.firstName?.trim() || profile.lastName?.trim());
        if (hasSavedProfile) {
          setFirstName(profile.firstName ?? '');
          setLastName(profile.lastName ?? '');
          setCity(profile.city ?? '');
          setCountry(profile.country ?? '');
          setBio(profile.bio ?? '');
          setAvatarUrl(profile.avatarUrl);
          setChessRating(profile.chessRating != null ? String(profile.chessRating) : '');
          setChessFideId(profile.chessFideId != null ? String(profile.chessFideId) : '');
          setChessClub(profile.chessClub ?? '');
          setSex(profile.sex ?? '');
          setBirthYear(profile.birthYear != null ? String(profile.birthYear) : '');
          setChessTitle(profile.chessTitle ?? '');
          setBackgammonClub(profile.backgammonClub ?? '');
          setBmabTitle(profile.bmabTitle ?? '');
          return;
        }
        if (profile) setAvatarUrl(profile.avatarUrl);
        // No saved profile yet. If we're here via an invite, prefill from the
        // director-entered player row. The invite preview is game-agnostic;
        // we steer rating/FIDE ID/club into the chess section because the
        // invite flow today is chess-only in practice.
        if (invite) {
          const preview = await getInvitePreview(invite).catch(() => null);
          if (preview) {
            setFirstName(preview.playerFirstName ?? '');
            setLastName(preview.playerLastName ?? '');
            setCity(preview.playerCity ?? '');
            setCountry(preview.playerCountry ?? '');
            setChessRating(preview.playerRating != null ? String(preview.playerRating) : '');
            setChessFideId(preview.playerFideId != null ? String(preview.playerFideId) : '');
            setChessClub(preview.playerClub ?? '');
          }
          return;
        }
        // 006 follow-up — no saved profile, no invite. Fall back to
        // whatever the OAuth provider gave us so a first-time Google
        // sign-in doesn't make the user retype the name Google
        // already shared. Empty strings if nothing usable is there.
        const meta = (user?.user_metadata ?? null) as
          | Record<string, unknown>
          | null;
        const { firstName: prefillFirst, lastName: prefillLast } =
          prefillFromAuthMetadata(meta);
        if (prefillFirst) setFirstName(prefillFirst);
        if (prefillLast) setLastName(prefillLast);
      } catch (err) {
        if (err instanceof ApiError) showErrorToast(err.userMessage);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function validate(): boolean {
    const next: Record<string, string> = {};
    if (!firstName.trim()) next.firstName = t('profile.errors.first_name_required');
    if (!lastName.trim()) next.lastName = t('profile.errors.last_name_required');
    setErrors(next);
    return Object.keys(next).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const saved = await saveProfile({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        city: city.trim() || null,
        country: country.trim() || null,
        bio: bio.trim() || null,
        chessRating: chessRating ? Number(chessRating) : null,
        chessFideId: chessFideId ? Number(chessFideId) : null,
        chessClub: chessClub.trim() || null,
        sex: sex || null,
        birthYear: birthYear ? Number(birthYear) : null,
        chessTitle: chessTitle || null,
        backgammonClub: backgammonClub.trim() || null,
        bmabTitle: bmabTitle || null,
      });
      setFirstName(saved.firstName);
      setLastName(saved.lastName);
      setCity(saved.city ?? '');
      setCountry(saved.country ?? '');
      setBio(saved.bio ?? '');
      setAvatarUrl(saved.avatarUrl);
      setChessRating(saved.chessRating != null ? String(saved.chessRating) : '');
      setChessFideId(saved.chessFideId != null ? String(saved.chessFideId) : '');
      setChessClub(saved.chessClub ?? '');
      setSex(saved.sex ?? '');
      setBirthYear(saved.birthYear != null ? String(saved.birthYear) : '');
      setChessTitle(saved.chessTitle ?? '');
      setBackgammonClub(saved.backgammonClub ?? '');
      setBmabTitle(saved.bmabTitle ?? '');
      setErrors({});
      showSuccessToast(t('profile.save_success'));
      const invite = searchParams.get('invite');
      if (invite) {
        // Profile save was the final gate for accepting an invite — go
        // straight to accept and land on the player dashboard.
        try {
          await acceptInvite(invite);
          navigate('/player/dashboard', { replace: true });
        } catch {
          navigate(`/invite/${encodeURIComponent(invite)}`);
        }
        return;
      }
      const registerFor = searchParams.get('registerFor');
      if (registerFor) {
        await consumeRegisterForAfterProfileSave(registerFor, navigate);
        return;
      }
      // Story 051 follow-up — `?returnTo=` carries the user back to where
      // they came from (e.g. the public tournament page after they fixed
      // the missing required fields). No auto-enroll: registering is the
      // user's next explicit choice. Only accept relative same-origin
      // paths to keep the redirect free of open-redirect risk.
      // Security audit 2026-08-26 (FE-L1): the hand-rolled regex admitted
      // protocol-relative //evil.com; use the shared validator.
      const returnTo = searchParams.get('returnTo');
      if (returnTo && isSafeRedirectPath(returnTo)) {
        navigate(returnTo);
      }
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('profile.save_error'));
    } finally {
      setSubmitting(false);
    }
  }

  async function handleAvatarChange(file: File | null) {
    if (!file) return;
    setSubmitting(true);
    try {
      // Modern phone cameras can produce 8–12 MB JPEGs which blow past the
      // backend's 2 MB cap. Resize + recompress to keep avatars under ~1 MB
      // before sending. For images already small enough, this is still cheap.
      const upload = await compressForAvatar(file, t);
      const saved = await uploadProfileAvatar(upload);
      setAvatarUrl(saved.avatarUrl);
      showSuccessToast(t('profile.avatar_save_success'));
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else if (err instanceof Error) showErrorToast(err.message);
      else showErrorToast(t('profile.avatar_save_error'));
    } finally {
      setSubmitting(false);
      // Allow re-uploading the same file by clearing the input values.
      if (avatarInputRef.current) avatarInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
    }
  }

  async function handleAvatarRemove() {
    setSubmitting(true);
    try {
      const saved = await deleteProfileAvatar();
      setAvatarUrl(saved.avatarUrl);
      showSuccessToast(t('profile.avatar_remove_success'));
    } catch (err) {
      if (err instanceof ApiError) showErrorToast(err.userMessage);
      else showErrorToast(t('profile.avatar_remove_error'));
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <p>{t('profile.loading')}</p>
      </div>
    );
  }

  return (
    <div data-testid="profile-page" className="min-h-screen bg-background">
      <AppHeader />
      <div className="flex justify-center px-4 py-6 md:py-12">
        <Card className="w-full max-w-2xl">
          <CardHeader>
            <CardTitle>{t('profile.title')}</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} noValidate className="space-y-8">
              <ProfileFormFields
                values={{
                  firstName,
                  lastName,
                  city,
                  country,
                  bio,
                  avatarUrl,
                  chessRating,
                  chessFideId,
                  chessClub,
                  sex,
                  birthYear,
                  chessTitle,
                  backgammonClub,
                  bmabTitle,
                }}
                edit={{
                  errors,
                  submitting,
                  handlers: {
                    setFirstName,
                    setLastName,
                    setCity,
                    setCountry,
                    setBio,
                    setChessRating,
                    setChessFideId,
                    setChessClub,
                    setSex,
                    setBirthYear,
                    setChessTitle,
                    setBackgammonClub,
                    setBmabTitle,
                    avatarInputRef,
                    cameraInputRef,
                    onAvatarChange: handleAvatarChange,
                    onAvatarRemove: handleAvatarRemove,
                  },
                }}
              />

              <Button
                type="submit"
                size="lg"
                data-testid="profile-save-button"
                disabled={submitting}
                className="w-full sm:w-auto"
              >
                {submitting ? t('profile.saving') : t('profile.save')}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
